import {
    useMemo,
    useState,
    useEffect,
} from "react";

import {
    Box,
    Table,
    Text,
    Flex,
} from "@chakra-ui/react";

import Card from "../../views/Dashboard/components/Card";
import DataTableHeader from "./DataTableHeader";
import DataTablePagination from "./DataTablePagination";

const DataTable = ({
    title,
    total,
    data = [],
    columns = [],
    renderRow,
    filters = [],
    defaultFilter = "All",
    pageSize = 10,
    rightAction,
    loading = false,
    emptyMessage = "No records found.",
    emptyComponent,
    onFilterClick,
}) => {
    const [page, setPage] =
        useState(1);

    const [search, setSearch] =
        useState("");

    const [
        activeFilter,
        setActiveFilter,
    ] = useState(
        defaultFilter
    );

    useEffect(() => {
        setPage(1);
    }, [
        search,
        activeFilter,
    ]);

    const filteredData =
        useMemo(() => {
            let result = [
                ...data,
            ];

            if (
                activeFilter &&
                activeFilter !==
                    "All"
            ) {
                result =
                    result.filter(
                        item =>
                            item.status ===
                                activeFilter ||
                            item.filter ===
                                activeFilter
                    );
            }

            if (
                search.trim()
            ) {
                const term =
                    search.toLowerCase();

                result =
                    result.filter(
                        item =>
                            Object.values(
                                item
                            ).some(
                                value =>
                                    String(
                                        value ??
                                            ""
                                    )
                                        .toLowerCase()
                                        .includes(
                                            term
                                        )
                            )
                    );
            }

            return result;
        }, [
            data,
            activeFilter,
            search,
        ]);

    const totalPages =
        Math.max(
            1,
            Math.ceil(
                filteredData.length /
                    pageSize
            )
        );

    const paginatedData =
        useMemo(() => {
            const start =
                (page - 1) *
                pageSize;

            return filteredData.slice(
                start,
                start +
                    pageSize
            );
        }, [
            filteredData,
            page,
            pageSize,
        ]);

    return (
        <Card
            p={0}
            overflow="hidden"
        >
            <DataTableHeader
                title={title}
                total={
                    total ??
                    data.length
                }
                filters={filters}
                activeFilter={
                    activeFilter
                }
                onFilterChange={
                    value => {
                        setActiveFilter(
                            value
                        );
                    }
                }
                rightAction={
                    rightAction
                }
                onFilterClick={
                    onFilterClick
                }
                search={search}
                onSearchChange={
                    setSearch
                }
            />

            <Box
                overflowX="auto"
                sx={{
                    "&::-webkit-scrollbar":
                        {
                            height:
                                "8px",
                        },
                    "&::-webkit-scrollbar-thumb":
                        {
                            background:
                                "#CBD5E1",
                            borderRadius:
                                "999px",
                        },
                }}
            >
                <Table.Root
                    minW={{
                        base: "800px",
                        lg: "1000px",
                    }}
                    variant="line"
                >
                    <Table.Header bg="gray.50">
                        <Table.Row>
                            {columns.map(
                                column => (
                                    <Table.ColumnHeader
                                        key={
                                            column.key
                                        }
                                        textAlign={
                                            column.align ||
                                            "start"
                                        }
                                    >
                                        {
                                            column.label
                                        }
                                    </Table.ColumnHeader>
                                )
                            )}
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {!loading &&
                            paginatedData.map(
                                (
                                    row,
                                    index
                                ) =>
                                    renderRow(
                                        row,
                                        index
                                    )
                            )}

                        {!loading &&
                            paginatedData.length ===
                                0 && (
                                <Table.Row>
                                <Table.Cell
                                    colSpan={columns.length}
                                    p={0}
                                >
                                    {emptyComponent ? (
                                        emptyComponent
                                    ) : (
                                        <Flex
                                            py={20}
                                            direction="column"
                                            justify="center"
                                            align="center"
                                            gap={3}
                                        >
                                            <Text color="gray.500">
                                                {emptyMessage}
                                            </Text>
                                        </Flex>
                                    )}
                                </Table.Cell>
                            </Table.Row>
                            )}
                    </Table.Body>
                </Table.Root>
            </Box>

            <DataTablePagination
                page={page}
                pageSize={
                    pageSize
                }
                total={
                    filteredData.length
                }
                onPageChange={
                    value => {
                        if (
                            value >=
                                1 &&
                            value <=
                                totalPages
                        ) {
                            setPage(
                                value
                            );
                        }
                    }
                }
            />
        </Card>
    );
};

export default DataTable;